// AGC: TierComparison - Feature comparison table for all tiers
// AGC: Shows detailed feature comparison between FREE, PRO, PREMIUM

import React, { useState } from 'react'
import { 
  Check, 
  X, 
  Gift, 
  Star, 
  Crown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type SubscriptionTier, 
  FEATURE_COMPARISON,
  TIER_DISPLAY_INFO,
} from '../../types/subscription'

interface TierComparisonProps {
  highlightTier?: SubscriptionTier
  showCategories?: boolean
  compact?: boolean
}

/**
 * AGC: Category labels for display - now using translations
 */
const getCategoryLabels = (t: (key: string) => string): Record<string, { name: string; icon: string }> => ({
  limits: { name: t('subscription.comparison.categories.limits') || 'Limites', icon: '📊' },
  mapping: { name: t('subscription.comparison.categories.mapping') || 'Mapeamento', icon: '🗺️' },
  recommendations: { name: t('subscription.comparison.categories.recommendations') || 'Recomendações', icon: '💡' },
  weather: { name: t('subscription.comparison.categories.weather') || 'Clima', icon: '🌤️' },
  voice: { name: t('subscription.comparison.categories.voice') || 'Voz & WhatsApp', icon: '🎤' },
  hardware: { name: t('subscription.comparison.categories.hardware') || 'Hardware', icon: '📡' },
  advisor: { name: t('subscription.comparison.categories.advisor') || 'Consultor Humano', icon: '👨‍🌾' },
  support: { name: t('subscription.comparison.categories.support') || 'Suporte', icon: '🆘' },
})

/**
 * AGC: Icon component for tiers
 */
const TierIcon: React.FC<{ tier: SubscriptionTier; className?: string }> = ({ tier, className = 'w-5 h-5' }) => {
  switch (tier) {
    case 'free':
      return <Gift className={className} />
    case 'pro':
      return <Star className={className} />
    case 'premium':
      return <Crown className={className} />
  }
}

/**
 * AGC: Value cell component
 */
const ValueCell: React.FC<{
  value: string | boolean
  isCurrentTier?: boolean
}> = ({ value, isCurrentTier }) => {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className="w-5 h-5 text-emerald-500 mx-auto" />
    ) : (
      <X className="w-5 h-5 text-gray-300 dark:text-slate-600 mx-auto" />
    )
  }

  return (
    <span className={`text-sm ${isCurrentTier ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
      {value}
    </span>
  )
}

/**
 * AGC: Category section component
 */
const CategorySection: React.FC<{
  category: string
  features: typeof FEATURE_COMPARISON
  currentTier: SubscriptionTier
  highlightTier?: SubscriptionTier
  defaultOpen?: boolean
}> = ({ category, features, currentTier, highlightTier, defaultOpen = true }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const categoryInfo = getCategoryLabels(t)[category]

  return (
    <div className="border-b border-gray-200 dark:border-slate-700 last:border-b-0">
      {/* AGC: Category header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
      >
        <span className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-300">
          <span>{categoryInfo?.icon}</span>
          {categoryInfo?.name || category}
        </span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* AGC: Features */}
      {isOpen && (
        <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
          {features.map((feature, index) => (
            <div key={index} className="grid grid-cols-4 gap-2 px-4 py-3 items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {feature.feature}
              </div>
              <div className={`text-center ${highlightTier === 'free' || currentTier === 'free' ? 'bg-emerald-50/50 dark:bg-emerald-900/10 -mx-2 px-2 py-1 rounded' : ''}`}>
                <ValueCell 
                  value={feature.free} 
                  isCurrentTier={currentTier === 'free'}
                />
              </div>
              <div className={`text-center ${highlightTier === 'pro' || currentTier === 'pro' ? 'bg-blue-50/50 dark:bg-blue-900/10 -mx-2 px-2 py-1 rounded' : ''}`}>
                <ValueCell 
                  value={feature.pro}
                  isCurrentTier={currentTier === 'pro'}
                />
              </div>
              <div className={`text-center ${highlightTier === 'premium' || currentTier === 'premium' ? 'bg-yellow-50/50 dark:bg-yellow-900/10 -mx-2 px-2 py-1 rounded' : ''}`}>
                <ValueCell 
                  value={feature.premium}
                  isCurrentTier={currentTier === 'premium'}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * AGC: Tier Comparison Component
 */
export const TierComparison: React.FC<TierComparisonProps> = ({
  highlightTier,
  showCategories = true,
  compact = false,
}) => {
  const { t } = useTranslation()
  const { tier: currentTier } = useSubscription()

  // AGC: Group features by category
  const groupedFeatures = FEATURE_COMPARISON.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = []
    }
    acc[feature.category].push(feature)
    return acc
  }, {} as Record<string, typeof FEATURE_COMPARISON>)

  const categories = Object.keys(groupedFeatures)

  if (compact) {
    // AGC: Compact view - just key features
    const keyFeatures = FEATURE_COMPARISON.filter(f => 
      f.category === 'limits' || f.category === 'voice' || f.category === 'advisor'
    ).slice(0, 6)

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700">
              <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-gray-400">
                Recurso
              </th>
              <th className="text-center py-2 px-2">
                <span className="text-emerald-600">Gratuito</span>
              </th>
              <th className="text-center py-2 px-2">
                <span className="text-blue-600">Pro</span>
              </th>
              <th className="text-center py-2 px-2">
                <span className="text-yellow-600">Premium</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {keyFeatures.map((feature, index) => (
              <tr key={index}>
                <td className="py-2 px-2 text-gray-600 dark:text-gray-400">
                  {feature.feature}
                </td>
                <td className="py-2 px-2 text-center">
                  <ValueCell value={feature.free} isCurrentTier={currentTier === 'free'} />
                </td>
                <td className="py-2 px-2 text-center">
                  <ValueCell value={feature.pro} isCurrentTier={currentTier === 'pro'} />
                </td>
                <td className="py-2 px-2 text-center">
                  <ValueCell value={feature.premium} isCurrentTier={currentTier === 'premium'} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
      {/* AGC: Header with tier names */}
      <div className="grid grid-cols-4 gap-2 px-4 py-4 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('subscription.features') || 'Recursos'}
        </div>
        
        {/* AGC: Free tier header */}
        <div className={`text-center ${currentTier === 'free' ? 'relative' : ''}`}>
          {currentTier === 'free' && (
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">
              Atual
            </div>
          )}
          <div className={`flex items-center justify-center gap-1 ${TIER_DISPLAY_INFO.free.color}`}>
            <TierIcon tier="free" className="w-4 h-4" />
            <span className="font-semibold">Gratuito</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">R$ 0</p>
        </div>
        
        {/* AGC: Pro tier header */}
        <div className={`text-center ${currentTier === 'pro' ? 'relative' : ''}`}>
          {currentTier === 'pro' && (
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
              Atual
            </div>
          )}
          <div className={`flex items-center justify-center gap-1 ${TIER_DISPLAY_INFO.pro.color}`}>
            <TierIcon tier="pro" className="w-4 h-4" />
            <span className="font-semibold">Pro</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">R$ 99/mês</p>
        </div>
        
        {/* AGC: Premium tier header */}
        <div className={`text-center ${currentTier === 'premium' ? 'relative' : ''}`}>
          {currentTier === 'premium' && (
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs bg-yellow-500 text-white px-2 py-0.5 rounded-full">
              Atual
            </div>
          )}
          <div className={`flex items-center justify-center gap-1 ${TIER_DISPLAY_INFO.premium.color}`}>
            <TierIcon tier="premium" className="w-4 h-4" />
            <span className="font-semibold">Premium</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">R$ 299/mês</p>
        </div>
      </div>

      {/* AGC: Feature categories */}
      {showCategories ? (
        <div>
          {categories.map((category, index) => (
            <CategorySection
              key={category}
              category={category}
              features={groupedFeatures[category]}
              currentTier={currentTier}
              highlightTier={highlightTier}
              defaultOpen={index < 3} // AGC: First 3 categories open by default
            />
          ))}
        </div>
      ) : (
        // AGC: Flat list without categories
        <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
          {FEATURE_COMPARISON.map((feature, index) => (
            <div key={index} className="grid grid-cols-4 gap-2 px-4 py-3 items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {feature.feature}
              </div>
              <div className="text-center">
                <ValueCell value={feature.free} isCurrentTier={currentTier === 'free'} />
              </div>
              <div className="text-center">
                <ValueCell value={feature.pro} isCurrentTier={currentTier === 'pro'} />
              </div>
              <div className="text-center">
                <ValueCell value={feature.premium} isCurrentTier={currentTier === 'premium'} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TierComparison

